<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, invoiceStatusClass, invoiceStatusLabel } from '../utils/format'
import type { CurrencyOption, Invoice, InvoiceStatus, Option } from '../types/finance'

const { t } = useI18n()
const { listInvoices, listCurrencies, listCounterpartyOptions } = useFinanceApi()

const statuses: InvoiceStatus[] = ['draft', 'unpaid', 'partial', 'paid']
const PER_PAGE = 20

const rows = ref<Invoice[]>([])
const currencies = ref<CurrencyOption[]>([])
const customers = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const page = ref(1)

const filters = ref({
  agent_id: '' as string | number,
  status: '' as InvoiceStatus | '',
  currency_code: '',
  search: '',
})

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter((invoice) =>
    [invoice.agent_name, String(invoice.id), invoice.order_id, invoice.shipment_id]
      .filter((value) => value !== null && value !== undefined)
      .some((value) => String(value).toLowerCase().includes(term)),
  )
})

const totalPages = computed(() => Math.max(1, Math.ceil(visibleRows.value.length / PER_PAGE)))
const pagedRows = computed(() =>
  visibleRows.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
)

const rangeLabel = computed(() => {
  const total = visibleRows.value.length
  if (!total) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total)
  return t('finance.common.showing', { from, to, total })
})

const totalsByCurrency = computed(() => {
  const totals = new Map<string, number>()
  for (const invoice of visibleRows.value) {
    const code = invoice.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(invoice.total_amount ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listInvoices({
      agent_id: filters.value.agent_id,
      status: filters.value.status,
      currency_code: filters.value.currency_code,
    })
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  const [currencyResult, customerResult] = await Promise.allSettled([
    listCurrencies(),
    listCounterpartyOptions(),
  ])
  currencies.value = currencyResult.status === 'fulfilled' ? currencyResult.value : []
  customers.value = customerResult.status === 'fulfilled' ? customerResult.value : []
}

watch(
  () => [filters.value.agent_id, filters.value.status, filters.value.currency_code],
  () => {
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    page.value = 1
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.receivables.title') }}</h2>
  </div>

  <div class="toolbar">
    <select v-model="filters.agent_id" class="filter-select">
      <option value="">{{ t('finance.common.agent') }}</option>
      <option v-for="customer in customers" :key="customer.id" :value="customer.id">
        {{ customer.name }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option v-for="status in statuses" :key="status" :value="status">
        {{ t(`finance.invoiceStatus.${status}`) }}
      </option>
    </select>
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <span class="spacer" />
    <div class="summary">
      <span v-for="line in totalsByCurrency" :key="line.code">
        <strong>{{ formatMoney(line.amount, line.code) }}</strong>
      </span>
      <span>{{ t('finance.common.rows', { count: visibleRows.length }) }}</span>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.receivables.empty') }}</p>
  </div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.invoice') }}</th>
            <th>{{ t('finance.common.agent') }}</th>
            <th>{{ t('finance.receivables.order') }}</th>
            <th>{{ t('finance.receivables.shipment') }}</th>
            <th>{{ t('finance.common.currency') }}</th>
            <th class="cell-num">{{ t('finance.receivables.amount') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th>{{ t('finance.receivables.created') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in pagedRows" :key="invoice.id">
            <td class="cell-code">#{{ invoice.id }}</td>
            <td class="cell-name">{{ invoice.agent_name || invoice.agent_id }}</td>
            <td>{{ invoice.order_id ?? '—' }}</td>
            <td>{{ invoice.shipment_id ?? '—' }}</td>
            <td>{{ invoice.currency_code }}</td>
            <td class="cell-num">
              {{ formatMoney(invoice.total_amount, invoice.currency_code) }}
            </td>
            <td>
              <span class="status-badge" :class="invoiceStatusClass(invoice.status)">
                {{ invoiceStatusLabel(t, invoice.status) }}
              </span>
            </td>
            <td class="cell-date">{{ formatDisplayDate(invoice.created_at) }}</td>
            <td class="cell-actions">
              <RouterLink class="link-action" :to="`/receivables/invoices/${invoice.id}`">
                {{ t('finance.common.open') }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1" @click="page -= 1">{{ t('finance.common.previous') }}</button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages" @click="page += 1">
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>
